/**
 * API Request and Response Types
 * Based on API documentation
 */

// ============================================
// Generic API Response Types
// ============================================

export interface ApiResponse<T = unknown> {
    status: 'success' | 'error';
    statusCode?: number;
    message: string;
    data?: T;
}

export interface ApiError {
    status: 'error';
    message: string;
    statusCode?: number;
}

// ============================================
// Authentication Types
// ============================================

export interface SendOTPRequest {
    phone: string;
}

export interface SendOTPResponse {
    status: 'success';
    message: string;
}

export interface VerifyOTPRequest {
    phone: string;
    otp: string;
}

export interface AdminUser {
    _id: string;
    name: string;
    phone: string;
    status: string;
}

export interface VerifyOTPResponse {
    status: 'success';
    statusCode: number;
    message: string;
    data: {
        token: string;
        adminUser: AdminUser;
    };
}

// ============================================
// Microsite Types
// ============================================

export interface MicrositeListItem {
    _id: string;
    storeName: string;
    brandName: string;
    storeBrandSlug: string;
    storeSlug?: string;
    url: string;
    status: 'Draft' | 'Published' | 'Inactive';
    createdAt: string;
    updatedAt: string;
}

export interface PaginationResponse {
    total: number;
    totalPages: number;
    skip: number;
    limit: number;
}

export interface MicrositeListResponse {
    status: 'success';
    message: string;
    data: {
        microsites: MicrositeListItem[];
        pagination: PaginationResponse;
    };
}

export interface MicrositeStats {
    total: number;
    live: number;
    draft: number;
    inactive: number;
}

export interface MicrositeStatsResponse {
    status: 'success';
    message: string;
    data: MicrositeStats;
}

// Microsite Detail Types
export interface MetaData {
    title?: string;
    description?: string;
    keywords?: string[]; // Array of keywords from API
    ogImage?: string;
}

export interface ImageWithAlt {
    url: string;
    altText?: string;
}

export interface HeroTag {
    label?: string;
    description?: string;
}

export interface HeroSection {
    imageUrls?: ImageWithAlt[];
    tags?: HeroTag[];
}

export interface HeroBanner {
    title?: string;
    subtitle?: string;
    highlightLabel?: string;
    ctaText?: string;
    redirectionUrl?: string;
}

export interface ModalConfig {
    headerBadge?: string;
    headerTitle?: string;
    headerSubtitle?: string;
    primaryButtonText?: string;
    primaryButtonInfo?: string;
    footerNote?: string;
}

export interface LocationInfo {
    holidayInfo?: string;
}

export interface GallerySection {
    imageUrls?: ImageWithAlt[];
}

export interface DealItem {
    giftItem?: string;
    limitedOffer?: boolean;
    spendAmount?: number;
    validity?: string; // ISO 8601 date string
}

export interface DealsSection {
    items?: DealItem[];
}

export interface SocialPlatform {
    username?: string;
    followers?: number;
}

export interface SocialLinks {
    facebook?: SocialPlatform;
    instagram?: SocialPlatform;
}

export interface Footer {
    licenseNumber?: string;
    minimumLegalDrinkingAge?: number;
}

export interface GeoLocation {
    type: 'Point';
    coordinates?: [number, number]; // [longitude, latitude]
    validity?: string; // ISO 8601 date string
}

export interface Timing {
    day: number; // 1-7 (Monday-Sunday)
    openingTime: string; // ISO 8601 format
    closingTime: string; // ISO 8601 format
}

export interface LocationCoordinates {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
}

export interface StoreOperationsConfig {
    timings?: Timing[];
    phone?: string | null;
    whatsappNumber?: string | null;
    location?: LocationCoordinates | null;
    city?: string | null;
    landmark?: string | null;
    nearbyAreas?: string[];
}

export interface StoreBrandOperationsConfig {
    phone?: string | null;
    whatsappNumber?: string | null;
}

// Admin operations config aliases (used by store-management admin APIs)
export interface OperationsTiming {
    day: number; // 1=Monday ... 7=Sunday
    openingTime: string;
    closingTime: string;
}

export interface OperationsLocation {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
}

export interface GetStoreOperationsConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: { operationsConfig: StoreOperationsConfig | null };
}

export interface GetStoreBrandOperationsConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: { operationsConfig: StoreBrandOperationsConfig | null };
}

export interface StoreBrandReference {
    _id: string;
    name: string;
}

export interface StoreReference {
    _id: string;
    name: string;
}

export interface MicrositeDetail {
    _id: string;
    storeBrandSlug: string;
    storeSlug?: string;
    storeBrand: StoreBrandReference;
    store?: StoreReference;
    status: 'Draft' | 'Published' | 'Inactive'; // Microsite publication status
    storeStatus?: 'Open' | 'Closed'; // Store operational status (only for store-level microsites)
    tagline?: string;
    logo?: string;
    metaData?: MetaData;
    hero?: HeroSection;
    heroBanner?: HeroBanner;
    modal?: ModalConfig;
    location?: LocationInfo;
    gallery?: GallerySection;
    deals?: DealsSection;
    social?: SocialLinks;
    footer?: Footer;
    geoLocation?: GeoLocation;
    stores?: unknown[]; // Array of related stores
    timings?: Timing[];
    phone?: string;
    whatsappNumber?: string;
    storeOperationsConfig?: StoreOperationsConfig;
    storeBrandOperationsConfig?: StoreBrandOperationsConfig;
    createdAt: string;
    updatedAt: string;
}

export interface MicrositeDetailResponse {
    status: 'success';
    message: string;
    data: MicrositeDetail;
}

// ============================================
// Microsite Save/Update Types
// ============================================

export interface MicrositeData {
    tagline?: string;
    logo?: string;
    metaData?: MetaData;
    hero?: HeroSection;
    heroBanner?: HeroBanner;
    modal?: ModalConfig;
    location?: LocationInfo;
    gallery?: GallerySection;
    deals?: DealsSection;
    social?: SocialLinks;
    footer?: Footer;
    geoLocation?: GeoLocation;
}

export interface SaveMicrositeRequest {
    micrositeID?: string; // Required for updates, omit for new microsites
    storeBrandID: string;
    storeBrandSlug: string;
    storeID?: string;
    storeSlug?: string;
    action: 'saveDraft' | 'publish';
    micrositeData: MicrositeData;
    storeOperationsConfig?: StoreOperationsConfig;
    storeBrandOperationsConfig?: StoreBrandOperationsConfig;
}

export interface SaveMicrositeResponse {
    status: 'success';
    message: string;
    data: {
        storeBrandID: string;
        storeID?: string;
        status: 'Draft' | 'Published';
    };
}

// ============================================
// Status Update Types
// ============================================

export interface UpdateStatusRequest {
    storeBrandID: string;
    storeID?: string;
    status: 'Published' | 'Inactive';
}

export interface UpdateStatusResponse {
    status: 'success';
    message: string;
    data: {
        storeBrandID: string;
        storeID?: string | null;
        status: 'Published' | 'Inactive';
        message: string;
    };
}

// ============================================
// Image Upload Types
// ============================================

export interface UploadFileInfo {
    fileName: string;
    fileType: string; // 'image/jpeg' | 'image/png' | 'image/webp'
}

export interface UploadURLsRequest {
    storeBrandID: string;
    storeID?: string;
    section: 'logo' | 'ogImage' | 'hero' | 'gallery' | 'modal';
    files: UploadFileInfo[];
}

export interface UploadURLData {
    fileName: string;
    uploadUrl: string;
    cloudFrontUrl: string;
    key: string;
}

export interface UploadURLsResponse {
    status: 'success';
    message: string;
    data: {
        files: UploadURLData[];
        expiresIn: number; // seconds (300 = 5 minutes)
    };
}

// ============================================
// Sorting and Filters Types
// ============================================

export interface SortOption {
    key: string;
    sortOrder: 1 | -1;
}

export interface SortingFiltersResponse {
    status: 'success';
    message: string;
    data: {
        sorting: Record<string, SortOption>;
        filters: {
            status: string[];
        };
    };
}

// ============================================
// Query Parameters Types
// ============================================

export interface MicrositeListParams {
    skip?: number;
    limit?: number;
    sortValue?: string;
    status?: 'Draft' | 'Published' | 'Inactive';
    storeBrand?: string;
    search?: string;
}

export interface MicrositeStatsParams {
    storeBrand?: string;
}

// ============================================
// Search APIs
// ============================================

/**
 * Search Store Brands Request
 */
export interface SearchStoreBrandsRequest {
    searchText: string;
}

/**
 * Search Store Brands Response
 */
export interface StoreBrandSearchResult {
    _id: string;
    name: string;
}

export interface SearchStoreBrandsResponse {
    status: string;
    message: string;
    data: StoreBrandSearchResult[];
}

/**
 * Search Stores Request
 */
export interface SearchStoresRequest {
    storeBrandID: string;
    searchText: string;
}

/**
 * Search Stores Response
 */
export type StoreLiveStatus = 'Active' | 'Trial' | 'Inactive';

export interface StoreSearchResult {
    _id: string;
    name: string;
    storeStatus?: StoreLiveStatus;
}

export interface SearchStoresResponse {
    status: string;
    message: string;
    data: StoreSearchResult[];
}

// ============================================
// Store Management API Types
// ============================================

export interface GetStoresByBrandResponse {
    status: string;
    data: StoreSearchResult[];
    message: string;
}

export interface UpdateStoreLiveStatusRequest {
    storeStatus: StoreLiveStatus;
    storeBrandId: string;
}

export interface UpdateStoreLiveStatusResponse {
    status: string;
    data: {
        _id: string;
        name: string;
        storeStatus: StoreLiveStatus;
    };
    message: string;
}

export interface RebuildClosingStockResponse {
    storeId: string;
    storeName: string;
    fromDate: string;
    toDate: string;
    previousDayClosingStockDate: string;
    daysProcessed: number;
    totalProcessed: number;
    totalModified: number;
    totalUpserted: number;
    failedBatches: number;
    salesRecords: number;
    wholesaleSalesRecords: number;
    purchaseRecords: number;
    transfersInRecords: number;
    transfersOutRecords: number;
    inStoreLossRecords: number;
    previousDayStockProducts: number;
    verificationsFound: number;
    foldedArchivePairs?: number;
    foldedSourceProducts?: number;
    error?: string;
}

export interface RebuildClosingStockApiResponse {
    status: 'success' | 'error' | 'failure';
    message: string;
    data: RebuildClosingStockResponse;
}

export interface UpdateTodayStockCountResponse {
    store: { id: string; name: string };
    date: string;
    summary: {
        totalProducts: number;
        productsNeedingUpdate: number;
        productsAlreadyCorrect: number;
        productsUpdated: number;
        totalPurchasesToday: number;
        totalSalesToday: number;
        totalTransfersInToday: number;
        totalTransfersOutToday: number;
        totalInStoreLossesToday: number;
    };
}

export interface UpdateTodayStockCountApiResponse {
    status: 'success' | 'error' | 'failure';
    message: string;
    data: UpdateTodayStockCountResponse;
}

// ============================================
// Blog API Types
// ============================================

export interface BlogListItem {
    _id: string;
    title: string;
    slug: string;
    subtitle: string;
    imageUrl: string;
    status: 'Active' | 'Inactive';
    createdAt: string;
    updatedAt: string;
}

export interface BlogDetail extends BlogListItem {
    content: string | null;
}

export interface BlogListParams {
    page?: number;
    sortValue?: string;
    status?: 'Active' | 'Inactive';
}

export interface BlogListPagination {
    totalPages: number;
    skip: number;
    limit: number;
    totalCount: number;
}

export interface BlogListResponse {
    blogs: BlogListItem[];
    pagination: BlogListPagination;
}

export interface BlogSortingFiltersResponse {
    sortingOptions: string[];
    statusOptions: string[];
}

export interface BlogImageUploadUrlRequest {
    fileType: 'image/jpeg' | 'image/png' | 'image/webp';
}

export interface BlogImageUploadUrlResponse {
    blogId: string;
    uploadUrl: string;
    cloudFrontUrl: string;
    expiresIn: number;
}

export interface CreateBlogRequest {
    _id: string;
    title: string;
    subtitle: string;
    content: string;
    imageUrl: string;
}

export interface UpdateBlogRequest {
    _id: string;
    title?: string;
    subtitle?: string;
    content?: string;
    imageUrl?: string;
}

export interface ToggleBlogStatusResponse {
    status: string;
    data: { status: string };
    message: string;
}

// ============================================
// SSE Event Types
// ============================================

export type AdminTriggerEventType =
    | 'FORCE_REFRESH_SALE_LIST'
    | 'REFRESH_SALE_CONFIG'
    | 'AUTO_RELOAD'
    | 'UPDATE_PRINTER_SERVICE'
    | 'SYNC_ORDERS'
    | 'SAVE_UNSYNCED_ORDERS'
    | 'PUSH_FRONTEND_LOGS';

export type AdminAllUsersTriggerEventType = Extract<
    AdminTriggerEventType,
    'AUTO_RELOAD' | 'UPDATE_PRINTER_SERVICE'
>;

export const ADMIN_TRIGGER_EVENTS: Record<AdminTriggerEventType, AdminTriggerEventType> = {
    FORCE_REFRESH_SALE_LIST: 'FORCE_REFRESH_SALE_LIST',
    REFRESH_SALE_CONFIG: 'REFRESH_SALE_CONFIG',
    AUTO_RELOAD: 'AUTO_RELOAD',
    UPDATE_PRINTER_SERVICE: 'UPDATE_PRINTER_SERVICE',
    SYNC_ORDERS: 'SYNC_ORDERS',
    SAVE_UNSYNCED_ORDERS: 'SAVE_UNSYNCED_ORDERS',
    PUSH_FRONTEND_LOGS: 'PUSH_FRONTEND_LOGS',
};

export const ADMIN_ALL_USERS_TRIGGER_EVENTS: Record<
    AdminAllUsersTriggerEventType,
    AdminAllUsersTriggerEventType
> = {
    AUTO_RELOAD: 'AUTO_RELOAD',
    UPDATE_PRINTER_SERVICE: 'UPDATE_PRINTER_SERVICE',
};

export interface TriggerStoreEventRequest {
    eventName: AdminTriggerEventType;
    storeIds?: string[];
    storeBrandId?: string;
    forceReload?: boolean;
    batchSize?: number;
    queue?: string;
    fromDate?: string;
    toDate?: string;
}

export interface TriggerStoreEventResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        eventName: AdminTriggerEventType;
        storeIds: string[];
        totalStores: number;
        eventTriggered: boolean;
    };
}

export interface TriggerAllUsersEventRequest {
    eventName: AdminAllUsersTriggerEventType;
    forceReload?: boolean;
}

export interface TriggerAllUsersEventResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        eventName: AdminAllUsersTriggerEventType;
        eventTriggered: boolean;
    };
}

// ============================================
// Unsynced Orders Types
// ============================================

export interface UnsyncedOrderItem {
    name: string;
    price: number;
}

export interface UnsyncedOrder {
    _id: string;
    orderID: string;
    offlineOrderID?: string | null;
    orderNetworkStatus?: string | null;
    errorType?: string | null;
    createdAt: string;
    orderAt: string;
    items: UnsyncedOrderItem[];
}

export interface GetUnsyncedOrdersRequest {
    storeId: string;
    fromDate: string;
    toDate: string;
}

export interface GetUnsyncedOrdersResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        orders: UnsyncedOrder[];
        total: number;
    };
}

export interface DeleteUnsyncedOrdersRequest {
    storeId: string;
    cartIds?: string[];
    errorType?: string;
    forceDelete?: boolean;
}

export interface DeleteUnsyncedOrdersResponse {
    status: 'success' | 'error';
    message: string;
    data: { storeId: string };
}

export interface UpdateUnsyncedOrdersRequest {
    storeId: string;
    cartIds: string[];
    newStoreId?: string;
    newStoreBrandId?: string;
}

export interface UpdateUnsyncedOrdersResponse {
    status: 'success' | 'error';
    message: string;
    data: { storeId: string };
}

// ============================================
// Brand Agent Config Types
// ============================================

export interface BrandAgentConfig {
    checkDeliveryRequest: boolean;
    checkBulkOrderRequest: boolean;
}

export interface GetBrandAgentConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        multiStoreAgentConfig: BrandAgentConfig;
    };
}

export interface SaveBrandAgentConfigRequest extends BrandAgentConfig {
    storeBrandId: string;
}

export interface SaveBrandAgentConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: BrandAgentConfig;
}

// ============================================
// Sale Completion WhatsApp Config Types
// ============================================

export interface SaleCompletionWhatsAppConfig {
    enabled: boolean;
    whatsappBusinessNumber: string | null;
}

export interface GetSaleCompletionWhatsAppConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        storeId: string;
        saleCompletionWhatsApp: SaleCompletionWhatsAppConfig;
    };
}

export interface UpdateSaleCompletionWhatsAppConfigRequest {
    saleCompletionWhatsApp: SaleCompletionWhatsAppConfig;
}

export interface UpdateSaleCompletionWhatsAppConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        storeId: string;
        saleCompletionWhatsApp: SaleCompletionWhatsAppConfig;
    };
}

// ============================================
// Multi-Store Agent Config Types (WhatsApp chatbot)
// ============================================

export interface OrderNotificationReceiver {
    storeMemberId: string;
    name: string | null;
    phone: string | null;
}

export interface MultiStoreAgentConfig {
    enabled: boolean;
    defaultStore: boolean;
    sendOrderRequestOnWhatsApp: boolean;
    orderNotificationReceivers: OrderNotificationReceiver[];
}

export interface MultiStoreAgentConfigPayload {
    enabled: boolean;
    defaultStore: boolean;
    sendOrderRequestOnWhatsApp: boolean;
    orderNotificationReceivers: string[];
}

export interface GetMultiStoreAgentConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        storeId: string;
        multiStoreAgentConfig: MultiStoreAgentConfig;
    };
}

export interface UpdateMultiStoreAgentConfigRequest {
    multiStoreAgentConfig: MultiStoreAgentConfigPayload;
}

export interface UpdateMultiStoreAgentConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        storeId: string;
        multiStoreAgentConfig: MultiStoreAgentConfig;
    };
}

// ============================================
// WhatsApp Business Number Types
// ============================================

export interface WhatsappBusinessNumber {
    _id: string;
    businessNumber: string;
    businessName: string;
    storeBrand?: string | null;
    isActive: boolean;
}

export interface GetWhatsappBusinessNumbersResponse {
    status: 'success' | 'error';
    message: string;
    data: WhatsappBusinessNumber[];
}

// ============================================
// Login Policy Types
// ============================================

export type DashboardRole =
    | 'Admin'
    | 'Manager'
    | 'Staff'
    | 'Cashier'
    | 'Super Admin'
    | 'Ghost'
    | 'Member';

export interface LoginPolicy {
    disallowedRolesOnDashboard?: DashboardRole[];
    disallowedRolesOnPOS?: DashboardRole[];
    enforceSingleDevice: boolean;
}

export interface GetLoginPolicyResponse {
    status: 'success' | 'error';
    message: string;
    data: LoginPolicy;
}

export interface SaveLoginPolicyRequest {
    enforceSingleDevice: boolean;
}

export interface MovementCountConfig {
    enabled: boolean;
}

export interface GetMovementCountConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: MovementCountConfig;
}

export type SaveMovementCountConfigRequest = MovementCountConfig;

export interface SaveMovementCountConfigResponse {
    status: 'success' | 'error';
    message: string;
    data: MovementCountConfig;
}

// ============================================
// Dashboard Expiry Settings Types
// ============================================

/** Calendar-day expiry for a store brand's merchant dashboard (YYYY-MM-DD or null). */
export interface DashboardExpirySettings {
    expiryDate: string | null;
    warningMessage: string | null;
}

export interface GetDashboardExpiryResponse {
    status: 'success' | 'error';
    message: string;
    data: DashboardExpirySettings;
}

export type UpdateDashboardExpiryRequest = DashboardExpirySettings & {
    makeLiveStoreIds?: string[];
};

export interface UpdateDashboardExpiryResponse {
    status: 'success' | 'error';
    message: string;
    data: DashboardExpirySettings;
}

// ============================================
// Daily Notification Settings Types
// ============================================

export type NotificationType = 'Consolidated Report' | 'Store Wise Report';
export type NotificationFileType = 'Excel' | 'PDF';

export const NOTIFICATION_TYPES: NotificationType[] = ['Consolidated Report', 'Store Wise Report'];
export const NOTIFICATION_FILE_TYPES: NotificationFileType[] = ['Excel', 'PDF'];

export interface NotificationReportEntry {
    reportId: string;
    notificationType: NotificationType;
    fileType?: NotificationFileType;
}

export interface DailyNotificationSettings {
    enabled: boolean;
    notificationReports: NotificationReportEntry[];
}

export interface GetDailyNotificationSettingsResponse {
    status: 'success' | 'error';
    message: string;
    data: DailyNotificationSettings;
}

export interface UpdateDailyNotificationSettingsResponse {
    status: 'success' | 'error';
    message: string;
    data: DailyNotificationSettings;
}

// ============================================
// Store Member Types (for notification receivers)
// ============================================

export interface StoreMember {
    _id: string;
    name: string;
    phone?: string;
    role?: DashboardRole;
}

export interface GetStoreMembersRequest {
    storeBrandID: string;
    storeID?: string;
    searchText?: string;
}

export interface GetStoreMembersResponse {
    status: 'success' | 'error';
    message: string;
    data: {
        storeMembers: StoreMember[];
        pagination: { total: number; totalPages: number; skip: number; limit: number };
    };
}

// ============================================
// AI Purchase Analytics Types
// (see dashboard-backend/docs/ai-purchase-tracking-plan.md §5 for the authoritative
// backend contract these types are built against)
// ============================================

/** AI-enabled states supported by the AI Purchase Analytics feature. */
export type AIPurchaseAnalyticsState =
    | 'Uttar Pradesh'
    | 'Assam'
    | 'Maharashtra'
    | 'Odisha'
    | 'Rajasthan';

export interface AIPurchaseDateRange {
    fromDate: string;
    toDate: string;
}

export interface AIPurchaseDateRangeParams {
    fromDate?: string;
    toDate?: string;
}

/**
 * Core metrics shared by every level (overall / state / brand / store).
 * Both adoption percentages are co-equal headline metrics - never drop either one.
 */
export interface AIPurchaseCoreMetrics {
    totalActivePurchases: number;
    totalLineItems: number;
    aiLinkedPurchases: number;
    aiLinkedLineItems: number;
    adoptionPercentByPurchases: number;
    adoptionPercentByLineItems: number;
}

export interface AIPurchaseOverallResponse {
    success: boolean;
    message: string;
    data: {
        dateRange: AIPurchaseDateRange;
        overall: AIPurchaseCoreMetrics;
    };
}

export interface AIPurchaseStateBreakdownItem extends AIPurchaseCoreMetrics {
    state: string;
}

export interface AIPurchaseByStateParams extends AIPurchaseDateRangeParams {
    state?: AIPurchaseAnalyticsState;
}

export interface AIPurchaseStateBreakdownResponse {
    success: boolean;
    message: string;
    data: {
        dateRange: AIPurchaseDateRange;
        states: AIPurchaseStateBreakdownItem[];
    };
}

export interface AIPurchaseBrandBreakdownItem extends AIPurchaseCoreMetrics {
    brandId: string;
    brandName: string;
}

/**
 * Search + pagination params for the by-state/:state/brands endpoint (added Jul 10
 * 2026, following the skip/limit/search convention used by getMicrositeList — see
 * dashboard-backend/docs/ai-purchase-tracking-plan.md §5.2).
 */
export interface AIPurchaseBrandListParams extends AIPurchaseDateRangeParams {
    search?: string;
    skip?: number;
    limit?: number;
    /** Server-side sort label; defaults to Adoption by Purchases (Low to High). */
    sortValue?: string;
}

export interface AIPurchaseBrandBreakdownResponse {
    success: boolean;
    message: string;
    data: {
        state: string;
        dateRange: AIPurchaseDateRange;
        brands: AIPurchaseBrandBreakdownItem[];
        pagination: PaginationResponse;
    };
}

export interface AIPurchaseStoreBreakdownItem extends AIPurchaseCoreMetrics {
    storeId: string;
    storeName: string;
    brandId?: string;
    brandName?: string;
    nonAiLinkedPurchases: number;
}

export interface AIPurchaseStoreParams extends AIPurchaseDateRangeParams {
    storeId?: string;
    brandId?: string;
}

export interface AIPurchaseStoreBreakdownResponse {
    success: boolean;
    message: string;
    data: {
        state: string;
        dateRange: AIPurchaseDateRange;
        stores: AIPurchaseStoreBreakdownItem[];
    };
}
